import type { ImportStatus, ImportPlatform } from './myGamesApi';
import type { PendingImport } from './pendingImportTracker';

export const IMPORT_POLL_FAST_MS = 4000;
export const IMPORT_POLL_SLOW_MS = 60_000;
export const IMPORT_POLL_FAST_WINDOW_MS = 600_000;
export const IMPORT_POLL_MAX_MS = 4 * 60 * 60 * 1000;

export function importPollDelayMs(startedAt: number, now = Date.now()): number {
  return now - startedAt < IMPORT_POLL_FAST_WINDOW_MS
    ? IMPORT_POLL_FAST_MS
    : IMPORT_POLL_SLOW_MS;
}

export function isActiveImportJob(updatedAt: string | undefined, now = Date.now()): boolean {
  if (!updatedAt) {
    return false;
  }
  const updatedAtMs = new Date(updatedAt).getTime();
  if (!Number.isFinite(updatedAtMs)) {
    return false;
  }
  const ageMs = now - updatedAtMs;
  return ageMs >= 0 && ageMs < IMPORT_POLL_MAX_MS;
}

export function platformLabel(platform: ImportPlatform): string {
  return platform === 'chesscom' ? 'Chess.com' : 'Lichess';
}

export function importFailureMessage(
  importJob: NonNullable<ImportStatus['importJob']>,
  fallbackPlatform: ImportPlatform,
  fallbackUsername: string,
): string {
  const username = importJob.username ?? fallbackUsername;
  const platform = importJob.platform ?? fallbackPlatform;

  if (importJob.error === 'user_not_found') {
    return `Could not find user "${username}" on ${platformLabel(platform)}. Check the username and try again.`;
  }

  if (importJob.error === 'timed_out') {
    return 'Import timed out after several hours. Please try again.';
  }

  return 'Import failed. Please try again.';
}

export function buildImportCompleteMessage(
  status: ImportStatus,
  pending: PendingImport,
): string {
  const added = status.importedGameCount - pending.priorCount;
  const { platform, username } = pending;

  if (added > 0) {
    return added === 1
      ? `Your games from ${username} are ready.`
      : `Imported ${added} new game(s) from ${platformLabel(platform)}.`;
  }

  if (status.atImportCap) {
    return `Import finished. Your library still has ${status.importGameLimit.toLocaleString()} games — unanalyzed games may have been removed to make room.`;
  }

  return `Import from ${platformLabel(platform)} finished.`;
}

export function importJobMatchesPending(
  importJob: ImportStatus['importJob'],
  pending: PendingImport,
): boolean {
  if (!importJob) {
    return false;
  }
  return (
    importJob.platform === pending.platform &&
    importJob.username?.toLowerCase() === pending.username.toLowerCase()
  );
}

export const IMPORT_POLL_TIMEOUT_MESSAGE =
  'Import timed out after several hours. Please try again.';
